import type { LayoutListDirection } from "./types.js";
import { resolvePath } from "./resolution.js";

/**
 * Derives a stable row key from a list item and optional dot-path.
 */
export function deriveLayoutListRowKey(
  item: unknown,
  rowKeyPath: string | undefined,
  index: number,
): string {
  if (!rowKeyPath) {
    return String(index);
  }

  const value = resolvePath(item, rowKeyPath);
  if (value == null) {
    return String(index);
  }

  return String(value);
}

export interface LayoutListRowOffsetInput {
  outerHeight: number;
  outerWidth: number;
}

export interface LayoutListRowWithOffsets extends LayoutListRowOffsetInput {
  offsetX: number;
  offsetY: number;
}

/**
 * Assigns absolute offsets for each row based on the list flow direction.
 */
export function resolveLayoutListRowOffsets<T extends LayoutListRowOffsetInput>(
  rows: T[],
  direction: LayoutListDirection = "column",
): Array<T & LayoutListRowWithOffsets> {
  if (direction === "column") {
    let offsetY = 0;
    return rows.map((row) => {
      const positioned = { ...row, offsetX: 0, offsetY };
      offsetY += row.outerHeight;
      return positioned;
    });
  }

  if (direction === "column-reverse") {
    const totalHeight = rows.reduce((sum, row) => sum + row.outerHeight, 0);
    let offsetFromBottom = totalHeight;
    return rows.map((row) => {
      offsetFromBottom -= row.outerHeight;
      return { ...row, offsetX: 0, offsetY: offsetFromBottom };
    });
  }

  if (direction === "row") {
    let offsetX = 0;
    return rows.map((row) => {
      const positioned = { ...row, offsetX, offsetY: 0 };
      offsetX += row.outerWidth;
      return positioned;
    });
  }

  const totalWidth = rows.reduce((sum, row) => sum + row.outerWidth, 0);
  let offsetFromRight = totalWidth;
  return rows.map((row) => {
    offsetFromRight -= row.outerWidth;
    return { ...row, offsetX: offsetFromRight, offsetY: 0 };
  });
}
